// @ts-check
// Aba Agenda do card, da pessoa e da família (doc 10 do mapeamento, seção 12): próximos eventos primeiro e os
// anteriores depois, cada um com o mesmo detalhe da página Agenda; no card, o botão Agendar.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { listCalendarEvents } from '../../data/calendar.js';
import { formatLongDate, formatTime } from '../../lib/calendar-dates.js';
import { getTagStyle } from '../../lib/theme-colors.js';
import { Button } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar } from '../../ui/snackbar.js';
import { CalendarEventDetail, calendarItemColor, calendarItemTitle, calendarSourceIcon } from './calendar-event-detail.js';
import { ScheduleEventModal } from './schedule-event-modal.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/calendar.js').CalendarItem} CalendarItem */

/** Objetos com a aba Agenda e o filtro da API de cada um. */
export const CALENDAR_TAB_FILTERS = Object.freeze({ enrollmentOpportunity: 'enrollmentOpportunityId', person: 'personId', household: 'householdId' });

/**
 * @param {{ item: CalendarItem, onSelect: (item: CalendarItem) => void }} props
 */
function RecordCalendarRow({ item, onSelect }) {
  const color = calendarItemColor(item);
  return html`<button type="button" class="record-calendar-row" data-canceled=${item.isCanceled ? 'true' : undefined} onClick=${() => onSelect(item)}>
    <span class="record-calendar-row-bar" style=${{ background: `var(--t-color-${color})` }}></span>
    <span class="record-calendar-row-when">
      <span>${formatLongDate(item.startsAt)}</span>
      <span class="calendar-detail-secondary">${item.isFullDay ? t('calendar.allDay') : `${formatTime(item.startsAt)} - ${formatTime(item.endsAt)}`}</span>
    </span>
    <span class="record-calendar-row-title">
      ${item.isRestricted ? html`<${Icon} name="IconLock" size=${14} />` : null}
      <span>${calendarItemTitle(item)}</span>
    </span>
    <span class="record-calendar-row-type" style=${getTagStyle(color)}><${Icon} name=${calendarSourceIcon(item)} size=${12} />${t(`calendar.eventTypes.${item.eventType}`)}</span>
  </button>`;
}

/**
 * @param {{ objectNameSingular: 'enrollmentOpportunity' | 'person' | 'household', record: any }} props
 */
export function RecordCalendarTab({ objectNameSingular, record }) {
  const [items, setItems] = useState(/** @type {CalendarItem[] | null} */ (null));
  const [selected, setSelected] = useState(/** @type {CalendarItem | null} */ (null));
  const [isScheduling, setIsScheduling] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const filterName = CALENDAR_TAB_FILTERS[objectNameSingular];

  useEffect(() => {
    let isCancelled = false;
    listCalendarEvents({ [filterName]: record.id, includeCanceled: true })
      .then((loaded) => {
        if (isCancelled) return;
        setItems(loaded);
        setSelected((current) => (current ? loaded.find((item) => item.id === current.id) ?? null : null));
      })
      .catch((error) => {
        if (!isCancelled) {
          showErrorSnackbar(error);
          setItems([]);
        }
      });
    return () => {
      isCancelled = true;
    };
  }, [record.id, reloadKey]);

  const now = new Date().toISOString();
  const upcoming = (items ?? []).filter((item) => item.endsAt >= now);
  const past = (items ?? []).filter((item) => item.endsAt < now).reverse();
  const reload = () => setReloadKey((value) => value + 1);

  return html`<div class="record-calendar" data-testid="record-calendar-tab">
    ${objectNameSingular === 'enrollmentOpportunity'
      ? html`<div class="record-calendar-toolbar">
          <${Button} title=${t('calendar.cardAction')} icon="IconCalendarPlus" variant="secondary" size="small" onClick=${() => setIsScheduling(true)} />
        </div>`
      : null}
    ${items === null
      ? html`<div class="record-calendar-list">${[0, 1].map((index) => html`<${Skeleton} key=${index} height=${40} />`)}</div>`
      : items.length === 0
        ? html`<${EmptyState} icon="IconCalendar" title=${t('calendar.recordTab')} subtitle=${t('calendar.recordTabEmpty')} />`
        : html`
            ${upcoming.length > 0
              ? html`<h3 class="record-calendar-heading">${t('calendar.upcoming')}</h3>
                  <div class="record-calendar-list">${upcoming.map((item) => html`<${RecordCalendarRow} key=${item.id} item=${item} onSelect=${setSelected} />`)}</div>`
              : null}
            ${past.length > 0
              ? html`<h3 class="record-calendar-heading">${t('calendar.past')}</h3>
                  <div class="record-calendar-list">${past.map((item) => html`<${RecordCalendarRow} key=${item.id} item=${item} onSelect=${setSelected} />`)}</div>`
              : null}
          `}
    ${selected ? html`<div class="record-calendar-detail"><${CalendarEventDetail} key=${selected.id} item=${selected} onClose=${() => setSelected(null)} onChanged=${reload} /></div>` : null}
    ${isScheduling
      ? html`<${ScheduleEventModal}
          opportunity=${{ id: record.id, name: record.name ?? '', ownerId: record.ownerId ?? null }}
          onClose=${() => setIsScheduling(false)}
          onSaved=${reload}
        />`
      : null}
  </div>`;
}
